/**
 * Workflow Settings Modal
 * Comprehensive settings management with tabs for Basic Settings, Analytics, and Marketplace
 */

import { useState, useEffect } from 'react';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { SavedWorkflow, WorkflowStorageUtils } from '../../utils/workflowStorage';
import { workflowAnalyticsService, WorkflowAnalytics } from '../../services/WorkflowAnalyticsService';

export interface WorkflowSettingsModalProps {
  isOpen: boolean;
  workflow: SavedWorkflow | null;
  onClose: () => void;
  onSave: (updatedWorkflow: SavedWorkflow) => void;
}

interface WorkflowSettings {
  name: string;
  description: string;
  tags: string[];
  isPublic: boolean;
  isFunction: boolean;
  enableAnalytics: boolean;
  autoSave: boolean;
  executionTimeout: number;
  maxRetries: number;
  notifyOnCompletion: boolean;
  notifyOnError: boolean;
}

export const WorkflowSettingsModal= ({
  isOpen,
  workflow,
  onClose,
  onSave
}: WorkflowSettingsModalProps) => {
  const { effectiveTheme } = useBootstrapTheme();
  const [activeTab, setActiveTab] = useState<'basic' | 'analytics' | 'marketplace'>('basic');
  const [settings, setSettings] = useState<WorkflowSettings>({
    name: '',
    description: '',
    tags: [],
    isPublic: false,
    isFunction: false,
    enableAnalytics: true,
    autoSave: true,
    executionTimeout: 300,
    maxRetries: 3,
    notifyOnCompletion: false,
    notifyOnError: true
  });
  const [analytics, setAnalytics] = useState<WorkflowAnalytics | null>(null);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);

  // Load workflow settings when workflow changes
  useEffect(() => {
    if (workflow) {
      setSettings({
        name: workflow.name,
        description: workflow.description || '',
        tags: workflow.metadata?.tags || [],
        isPublic: workflow.metadata?.isPublic || false,
        isFunction: workflow.isFunction,
        enableAnalytics: workflow.metadata?.enableAnalytics !== false,
        autoSave: workflow.metadata?.autoSave !== false,
        executionTimeout: workflow.metadata?.executionTimeout || 300,
        maxRetries: workflow.metadata?.maxRetries || 3,
        notifyOnCompletion: workflow.metadata?.notifyOnCompletion || false,
        notifyOnError: workflow.metadata?.notifyOnError !== false
      });

      // Load analytics data
      loadAnalytics();
    }
  }, [workflow]);

  const loadAnalytics = async () => {
    if (!workflow) return;

    try {
      const analyticsData = await workflowAnalyticsService.getWorkflowAnalytics(workflow.id);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to load workflow analytics:', error);
    }
  };

  const handleSave = () => {
    if (!workflow) return;

    setLoading(true);
    try {
      const updatedWorkflow: SavedWorkflow = {
        ...workflow,
        name: settings.name,
        description: settings.description,
        isFunction: settings.isFunction,
        metadata: {
          ...workflow.metadata,
          tags: settings.tags,
          isPublic: settings.isPublic,
          enableAnalytics: settings.enableAnalytics,
          autoSave: settings.autoSave,
          executionTimeout: settings.executionTimeout,
          maxRetries: settings.maxRetries,
          notifyOnCompletion: settings.notifyOnCompletion,
          notifyOnError: settings.notifyOnError,
          updatedAt: new Date().toISOString()
        }
      };

      // Save to storage
      const success = WorkflowStorageUtils.saveWorkflow(updatedWorkflow);
      if (success) {
        onSave(updatedWorkflow);
        onClose();
      } else {
        throw new Error('Failed to save workflow settings');
      }
    } catch (error) {
      console.error('Error saving workflow settings:', error);
      alert('Failed to save workflow settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !settings.tags.includes(newTag.trim())) {
      setSettings(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setSettings(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const renderBasicSettings = () => (
    <div className="tab-pane fade show active">
      <div className="row g-4">
        {/* General Information */}
        <div className="col-12">
          <h6 className="mb-3">📋 General Information</h6>
          
          <div className="mb-3">
            <label className="form-label">Workflow Name *</label>
            <input
              type="text"
              className="form-control"
              value={settings.name}
              onChange={(e) => setSettings(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter workflow name..."
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={3}
              value={settings.description}
              onChange={(e) => setSettings(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this workflow does..."
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Tags</label>
            <div className="input-group mb-2">
              <input
                type="text"
                className="form-control"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add tag..."
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
              />
              <button className="btn btn-outline-secondary" onClick={handleAddTag}>
                Add
              </button>
            </div>
            <div className="d-flex flex-wrap gap-1">
              {settings.tags.map((tag, index) => (
                <span key={index} className="badge bg-secondary">
                  {tag}
                  <button
                    className="btn-close btn-close-white ms-1"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleRemoveTag(tag)}
                  ></button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Execution Settings */}
        <div className="col-md-6">
          <h6 className="mb-3">⚙️ Execution Settings</h6>
          
          <div className="mb-3">
            <label className="form-label">Execution Timeout (seconds)</label>
            <input
              type="number"
              className="form-control"
              min="30"
              max="3600"
              value={settings.executionTimeout}
              onChange={(e) => setSettings(prev => ({ ...prev, executionTimeout: parseInt(e.target.value) || 300 }))}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Max Retries</label>
            <input
              type="number"
              className="form-control"
              min="0"
              max="10"
              value={settings.maxRetries}
              onChange={(e) => setSettings(prev => ({ ...prev, maxRetries: parseInt(e.target.value) || 3 }))}
            />
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="col-md-6">
          <h6 className="mb-3">🔧 Features</h6>
          
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={settings.isFunction}
              onChange={(e) => setSettings(prev => ({ ...prev, isFunction: e.target.checked }))}
            />
            <label className="form-check-label">
              <strong>Function Mode</strong>
              <br />
              <small className="text-muted">Make this workflow reusable as a function in other workflows</small>
            </label>
          </div>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={settings.autoSave}
              onChange={(e) => setSettings(prev => ({ ...prev, autoSave: e.target.checked }))}
            />
            <label className="form-check-label">
              <strong>Auto-Save</strong>
              <br />
              <small className="text-muted">Automatically save changes while editing</small>
            </label>
          </div>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={settings.enableAnalytics}
              onChange={(e) => setSettings(prev => ({ ...prev, enableAnalytics: e.target.checked }))}
            />
            <label className="form-check-label">
              <strong>Analytics Tracking</strong>
              <br />
              <small className="text-muted">Collect performance and usage analytics</small>
            </label>
          </div>
        </div>

        {/* Notifications */}
        <div className="col-12">
          <h6 className="mb-3">🔔 Notifications</h6>
          
          <div className="row">
            <div className="col-md-6">
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={settings.notifyOnCompletion}
                  onChange={(e) => setSettings(prev => ({ ...prev, notifyOnCompletion: e.target.checked }))}
                />
                <label className="form-check-label">
                  Notify on successful completion
                </label>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={settings.notifyOnError}
                  onChange={(e) => setSettings(prev => ({ ...prev, notifyOnError: e.target.checked }))}
                />
                <label className="form-check-label">
                  Notify on errors or failures
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="tab-pane fade">
      {analytics ? (
        <div className="row g-4">
          {/* Summary Cards */}
          <div className="col-12">
            <div className="row g-3">
              <div className="col-sm-6 col-lg-3">
                <div className="card bg-primary bg-opacity-10 border-primary border-opacity-25">
                  <div className="card-body text-center">
                    <div className="fs-4 fw-bold text-primary">{analytics.totalExecutions}</div>
                    <div className="small text-muted">Total Executions</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="card bg-success bg-opacity-10 border-success border-opacity-25">
                  <div className="card-body text-center">
                    <div className="fs-4 fw-bold text-success">{analytics.successfulExecutions}</div>
                    <div className="small text-muted">Successful</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="card bg-warning bg-opacity-10 border-warning border-opacity-25">
                  <div className="card-body text-center">
                    <div className="fs-4 fw-bold text-warning">{analytics.averageExecutionTime}ms</div>
                    <div className="small text-muted">Avg. Duration</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="card bg-info bg-opacity-10 border-info border-opacity-25">
                  <div className="card-body text-center">
                    <div className="fs-4 fw-bold text-info">{(analytics.successRate * 100).toFixed(1)}%</div>
                    <div className="small text-muted">Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts and Details */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">📊 Execution History</h6>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="small text-muted">Last 7 days</label>
                  <div className="d-flex align-items-end gap-1" style={{ height: '60px' }}>
                    {analytics.executionHistory.slice(-7).map((count, index) => (
                      <div
                        key={index}
                        className="bg-primary flex-fill"
                        style={{
                          height: `${(count / Math.max(...analytics.executionHistory.slice(-7))) * 100}%`,
                          minHeight: '2px'
                        }}
                        title={`${count} executions`}
                      ></div>
                    ))}
                  </div>
                </div>
                
                <div className="small text-muted">
                  <div>First execution: {new Date(analytics.firstExecution).toLocaleDateString()}</div>
                  <div>Last execution: {new Date(analytics.lastExecution).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">⚠️ Error Analysis</h6>
              </div>
              <div className="card-body">
                {analytics.errorTypes.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {analytics.errorTypes.map((error, index) => (
                      <div key={index} className="list-group-item px-0 py-2 border-0">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-medium">{error.type}</div>
                            <small className="text-muted">{error.message}</small>
                          </div>
                          <span className="badge bg-danger">{error.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted py-3">
                    <div className="fs-1">✅</div>
                    <div>No errors recorded</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">⚡ Performance Metrics</h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="text-center">
                      <div className="fs-5 fw-bold">{analytics.nodeExecutionStats.averageNodesPerExecution}</div>
                      <div className="small text-muted">Avg. Nodes/Execution</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-center">
                      <div className="fs-5 fw-bold">{analytics.nodeExecutionStats.slowestNodeAverage}ms</div>
                      <div className="small text-muted">Slowest Node Avg.</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-center">
                      <div className="fs-5 fw-bold">{analytics.resourceUsage.memoryUsage.toFixed(1)}MB</div>
                      <div className="small text-muted">Avg. Memory Usage</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3"></div>
          <div>Loading analytics...</div>
        </div>
      )}
    </div>
  );

  const renderMarketplace = () => (
    <div className="tab-pane fade">
      <div className="row g-4">
        {/* Publishing Settings */}
        <div className="col-12">
          <h6 className="mb-3">🏪 Marketplace Settings</h6>
          
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={settings.isPublic}
              onChange={(e) => setSettings(prev => ({ ...prev, isPublic: e.target.checked }))}
            />
            <label className="form-check-label">
              <strong>Public Workflow</strong>
              <br />
              <small className="text-muted">Make this workflow visible in the marketplace for others to discover and use</small>
            </label>
          </div>

          {settings.isPublic && (
            <div className="alert alert-info">
              <div className="d-flex align-items-start">
                <div className="me-2">ℹ️</div>
                <div>
                  <strong>Publishing Guidelines:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Ensure your workflow has a clear name and description</li>
                    <li>Add relevant tags to help users discover your workflow</li>
                    <li>Test your workflow thoroughly before publishing</li>
                    <li>Consider adding example inputs and expected outputs</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h6 className="card-title mb-0">📈 Marketplace Stats</h6>
            </div>
            <div className="card-body">
              <div className="row g-3 text-center">
                <div className="col-6">
                  <div className="fs-4 fw-bold text-primary">0</div>
                  <div className="small text-muted">Views</div>
                </div>
                <div className="col-6">
                  <div className="fs-4 fw-bold text-success">0</div>
                  <div className="small text-muted">Downloads</div>
                </div>
                <div className="col-6">
                  <div className="fs-4 fw-bold text-warning">0</div>
                  <div className="small text-muted">Stars</div>
                </div>
                <div className="col-6">
                  <div className="fs-4 fw-bold text-info">0</div>
                  <div className="small text-muted">Forks</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h6 className="card-title mb-0">🎯 Discoverability</h6>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label small">SEO Score</label>
                <div className="progress">
                  <div className="progress-bar bg-success" style={{ width: '75%' }}></div>
                </div>
                <small className="text-muted">Good - Add more tags to improve</small>
              </div>
              
              <div>
                <label className="form-label small">Category Match</label>
                <div className="progress">
                  <div className="progress-bar bg-primary" style={{ width: '60%' }}></div>
                </div>
                <small className="text-muted">Moderate - Consider adding category-specific tags</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen || !workflow) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className={`modal-content ${effectiveTheme === 'dark' ? 'bg-dark text-white' : ''}`}>
          {/* Header */}
          <div className="modal-header">
            <div>
              <h4 className="modal-title mb-1">⚙️ Workflow Settings</h4>
              <p className="text-muted mb-0">{workflow.name}</p>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          {/* Tabs */}
          <div className="modal-body p-0">
            <div className="border-bottom">
              <ul className="nav nav-tabs nav-justified" role="tablist">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'basic' ? 'active' : ''}`}
                    onClick={() => setActiveTab('basic')}
                  >
                    <span className="me-2">📋</span>
                    Basic Settings
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'analytics' ? 'active' : ''}`}
                    onClick={() => setActiveTab('analytics')}
                  >
                    <span className="me-2">📊</span>
                    Analytics
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'marketplace' ? 'active' : ''}`}
                    onClick={() => setActiveTab('marketplace')}
                  >
                    <span className="me-2">🏪</span>
                    Marketplace
                  </button>
                </li>
              </ul>
            </div>

            <div className="p-4">
              <div className="tab-content">
                {activeTab === 'basic' && renderBasicSettings()}
                {activeTab === 'analytics' && renderAnalytics()}
                {activeTab === 'marketplace' && renderMarketplace()}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleSave}
              disabled={loading || !settings.name.trim()}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <span className="me-2">💾</span>
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};